import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Send, Plus, FileArchive } from "lucide-react";
import { api, eur, errMsg } from "@/lib/api";
import InvoiceDialog from "@/pages/admin/InvoiceDialog";

export const downloadFile = async (url, name, params) => {
  try {
    const r = await api.get(url, { responseType: "blob", params });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(errMsg(e)); }
};

export default function AdminInvoices() {
  const [list, setList] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/invoices", { params: { month } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const resend = async (inv) => {
    try { await api.post(`/admin/invoices/${inv.id}/send`); toast.success(`Sąskaita išsiųsta: ${inv.buyer.email}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const sum = (list || []).reduce((s, i) => s + i.total, 0);

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display font-black uppercase text-4xl tracking-tight">Sąskaitos</h1>
        <div className="flex flex-wrap gap-2">
          <input data-testid="invoices-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="h-10 px-3 bg-[#1F2230] border border-[#3B4157] text-sm outline-none focus:border-orange-500" />
          <button data-testid="invoices-zip-btn" onClick={() => downloadFile("/admin/invoices/zip", `saskaitos_${month}.zip`, { month })} disabled={!list?.length}
            className="h-10 px-4 border border-[#3B4157] text-sm inline-flex items-center gap-2 hover:border-orange-500 hover:text-orange-500 disabled:opacity-40"><FileArchive className="w-4 h-4" /> Mėnesio archyvas (ZIP)</button>
          <button data-testid="invoice-new-btn" onClick={() => setOpen(true)}
            className="h-10 px-4 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 hover:bg-orange-400"><Plus className="w-4 h-4" /> Nauja sąskaita</button>
        </div>
      </div>
      <p className="text-sm text-slate-400">Perkant per sedynes.lt sąskaita išrašoma automatiškai: apmokėjus per Paysera arba pažymėjus COD užsakymą „Išsiųsta“. Klientui siunčiama nuoroda į PDF, jums – kopija el. paštu.</p>
      <div className="border border-[#3B4157] bg-[#292D3C] overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="text-[10px] uppercase tracking-[0.2em] font-mono-x text-slate-500 text-left">
            <tr><th className="p-3 pl-5">Nr.</th><th className="p-3">Data</th><th className="p-3">Pirkėjas</th><th className="p-3 text-right">Suma</th><th className="p-3">Būsena</th><th className="p-3 pr-5" /></tr>
          </thead>
          <tbody>
            {list?.map((i) => (
              <tr key={i.id} data-testid={`invoice-row-${i.number}`} className="border-t border-[#3B4157]">
                <td className="p-3 pl-5 font-mono-x text-orange-500">{i.number}</td>
                <td className="p-3 font-mono-x text-slate-400">{i.date}</td>
                <td className="p-3">{i.buyer.name}<div className="text-xs text-slate-500">{i.note || "Rankinė sąskaita"}</div></td>
                <td className="p-3 text-right font-mono-x">{eur(i.total)}</td>
                <td className="p-3 text-xs">{i.paid ? <span className="text-emerald-400">Apmokėta</span> : <span className="text-amber-400">Apmokėti iki {i.due_date || "—"}</span>}
                  <div className="text-slate-500">{i.sent_at ? `Išsiųsta ${i.sent_at.slice(0, 10)}` : "Neišsiųsta"}</div></td>
                <td className="p-3 pr-5 text-right whitespace-nowrap">
                  <button data-testid={`invoice-pdf-${i.number}`} onClick={() => downloadFile(`/admin/invoices/${i.id}/pdf`, `${i.number}.pdf`)} title="Atsisiųsti PDF" className="p-2 hover:text-orange-500"><Download className="w-4 h-4" /></button>
                  <button data-testid={`invoice-send-${i.number}`} onClick={() => resend(i)} disabled={!i.buyer.email} title="Siųsti klientui" className="p-2 hover:text-orange-500 disabled:opacity-30"><Send className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {list && !list.length && <tr><td colSpan={6} className="p-6 text-center text-slate-500">Šį mėnesį sąskaitų nėra</td></tr>}
          </tbody>
          {!!list?.length && <tfoot><tr className="border-t border-[#3B4157] font-mono-x"><td colSpan={3} className="p-3 pl-5 text-slate-400">Iš viso: {list.length}</td><td data-testid="invoices-total" className="p-3 text-right text-orange-500">{eur(sum)}</td><td colSpan={2} /></tr></tfoot>}
        </table>
      </div>
      <InvoiceDialog open={open} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />
    </div>
  );
}
